import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { t } from 'i18next'
import {
  FormInput,
  FormLabel,
  IconSelectionModal,
  ScriptValueDialog,
  Variable,
} from '@microservices/wiskey-react-components'
import { Box, Button, FormHelperText, IconButton, useTheme } from '@mui/material'
import { ClearIcon } from '@mui/x-date-pickers'

import { ErrorValidJSType } from '@helpers'
import { GENERATOR_INPUT_TYPE } from '@constants'

import { JSX_ICON_FIELDS_INFO, JSX_REGISTRY } from '@gantt/constants'

import { ICON_REGISTRY } from '../../constants/iconRegistry'
import { serializeIconToJsx } from '../../helpers/serializeIconToJsx'

import { EditorBase } from './EditorBase'

type ScriptValueEditorProps = {
  value: string
  path: string
  onChange: (value: string) => void
  label?: string
  language?: 'js' | 'json' | 'jsx'
  hint?: string
  validator?: (value: string) => ErrorValidJSType | undefined
  onAfterValidationScript?: (state: boolean) => void
  placeholder?: string
  noValidate?: boolean
  maxInputLength?: number
  hasError?: boolean
}

export interface JsxRegistryItem {
  /** Имя компонента, использующиеся в скрипте */
  name: string
  description?: string
  /** Функция, которая превращает пропсы в React-элемент */
  render: (props: any) => React.ReactElement
  /** Схема полей для автодополнения variables в ScriptValueDialog */
  schema: Variable[] | string
}

export type JsxRegistry = Record<string, JsxRegistryItem>

export const ScriptValueEditor = ({
  label,
  language = 'js',
  hint,
  onAfterValidationScript,
  hasError = false,
  value,
  path,
  onChange,
  ...otherProps
}: ScriptValueEditorProps) => {
  const theme = useTheme()

  const insertRef = useRef<((text: string) => void) | null>(null)

  const [invalid, setInvalid] = useState(false)
  const [isIconModalOpen, setIsIconModalOpen] = useState(false)

  const getStyle = useMemo(() => {
    return {
      width: '100%',
      border: invalid ? `1px solid ${theme.palette.error.main}` : theme.palette.border.field,
      borderRadius: '4px',
      transition: 'all .5s ease-in-out',
      flexGrow: 1,
    }
  }, [theme, invalid])

  const handleSetInvalid = useCallback((state: boolean) => {
    setInvalid(state)
    onAfterValidationScript?.(state)
  }, [])

  useEffect(() => {
    if (hasError) {
      setInvalid(true)
    } else {
      setInvalid(false)
    }
  }, [hasError])

  const [isJsxEditorOpen, setIsJsxEditorOpen] = useState(false)

  const defaultVariables: Variable[] = useMemo(() => {
    const components = Object.values(JSX_REGISTRY).map(item => ({
      name: item.name,
      description: item.description,
      // generateTypeDefinitionsFromVariables в react-components не может создавать React.FC<any>, описание FC недоступно
      type: 'any',
    }))

    const systemVars: Variable[] = [
      {
        name: 'srcObj',
        type: 'any',
        description: 'Объект с данными рассматриваемого элемента',
      },
    ]

    return [...components, ...systemVars]
  }, [])

  const editorComponents = {
    jsx: (
      <FormInput
        inputType={GENERATOR_INPUT_TYPE.INPUT}
        name={path}
        placeholder={t('commandParameters.inputs.value.placeholder')}
        readOnly={true}
        additionalBtn={{
          isEnabled: true,
          text: t('commandParameters.buttons.edit'),
          color: 'primary',
          variant: 'contained',
          onClick: () => {
            setIsJsxEditorOpen(true)
          },
        }}
        formInputContainerSx={{
          '& .MuiFormLabel-root': { display: 'none' },
          flexWrap: 'nowrap',
          '& .MuiButton-root': { ml: 0.5 },
        }}
        textFieldInputProps={{
          endAdornment: (
            <>
              <IconButton size='small' sx={{ width: 24 }} onClick={() => onChange('')}>
                <ClearIcon fontSize='small' />
              </IconButton>
            </>
          ),
        }}
      />
    ),
    js: (
      <EditorBase
        autoFocus={false}
        invalid={invalid}
        language={language}
        style={getStyle}
        value={value}
        onChange={onChange}
        onInvalid={handleSetInvalid}
        {...otherProps}
      />
    ),
    json: (
      <EditorBase
        autoFocus={false}
        invalid={invalid}
        language={language}
        style={getStyle}
        value={value}
        onChange={onChange}
        onInvalid={handleSetInvalid}
        {...otherProps}
      />
    ),
  }

  return (
    <>
      <Box display='flex'>
        <FormLabel
          label={label}
          name={''}
          control={
            <Box flexGrow={1}>
              {editorComponents[language] || null}
              {hint && <FormHelperText sx={{ pt: 0.5, pl: 0, ml: 0 }}>{hint}</FormHelperText>}
            </Box>
          }
        />
      </Box>

      <ScriptValueDialog
        btnText={t('parameters.visual.save')}
        fieldsInfo={JSX_ICON_FIELDS_INFO}
        hint={hint}
        isShow={isJsxEditorOpen}
        language={'jsx'}
        value={value}
        variables={defaultVariables}
        editorToolbar={insert => (
          <Button
            variant='outlined'
            onClick={() => {
              insertRef.current = insert
              setIsIconModalOpen(true)
            }}
          >
            {t('addIcon')}
          </Button>
        )}
        onClose={() => {
          setIsJsxEditorOpen(false)
        }}
        onSave={value => {
          onChange(value)
          setIsJsxEditorOpen(false)
        }}
      />

      <IconSelectionModal
        iconRegistry={ICON_REGISTRY}
        isOpen={isIconModalOpen}
        selectedIcon={null}
        onClose={() => setIsIconModalOpen(false)}
        onSave={selectedIcon => {
          if (selectedIcon) {
            insertRef.current?.(serializeIconToJsx(selectedIcon))
          }
          setIsIconModalOpen(false)
        }}
      />
    </>
  )
}
